// Sugalarm — Cycle 236: fullscreen reminder takeover overlay.
// Cycle 359: collapsed to a single Close action (the reminder Snooze was
// removed — a fired reminder is dismissed with one tap and is done).
//
// Renders when a `reminder_fired` frame arrives on any surface (kiosk, web,
// iOS, Android). It is the sole reminder display surface. Display-only: it
// never touches the alarm path, never silences an alarm, and is never
// paywalled. The only exit is Close (dismiss + hide) wired to the cycle-272
// cross-device dismiss so closing on one device clears the overlay on all of
// the owner's devices. No close/X-in-corner, no auto-dismiss timer.

import { useEffect, useRef, useState } from "react";

import type { Reminder } from "../../lib/types";
import styles from "./ReminderOverlay.module.css";

export interface ReminderOverlayProps {
  reminder: Reminder;
  /** Dismiss the fired reminder (Close). Wired in App.tsx to the local hide
   *  plus the cycle-272 cross-device dismiss fan-out. */
  onClose: () => void;
  /** Kiosk sizing overrides (1024×600 fit). */
  isKiosk?: boolean;
  /** Cycle 362: the time this occurrence was SCHEDULED for (e.g. "10:00 AM"),
   *  from the durable `reminder_fires` row. When present it replaces the live
   *  clock below.
   *
   *  Why: through cycle 361 this surface ticked `new Date()`, so the 3:13 PM
   *  resurface of a 10:00 AM reminder read "At 3:13 PM" above "Its 10am…" —
   *  it asserted it was firing NOW, which is much of why the incident read as
   *  a random pop-up. A reminder surface must name the time it was FOR.
   *  Absent only for a `/test` fire (no durable occurrence), where "now" IS
   *  the honest answer. */
  scheduledLabel?: string | null;
  /** Cycle 453.2 (operator decision 3, option a): the kiosk no-data banner is
   *  drawn ABOVE this takeover, on the bottom edge. Keep the Close button and
   *  the message clear of the band it covers (`--alarm-bottom-band`, published
   *  by the kiosk frame while the banner is lifted). */
  reserveAlarmBand?: boolean;
}

/** Current wall-clock time as "At h:mm A" (12-hour, e.g. "At 1:03 PM"). */
function formatClockTime(d: Date): string {
  const hours24 = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const mm = minutes < 10 ? `0${minutes}` : String(minutes);
  return `At ${hours12}:${mm} ${ampm}`;
}

export function ReminderOverlay({
  reminder,
  onClose,
  isKiosk = false,
  scheduledLabel = null,
  reserveAlarmBand = false,
}: ReminderOverlayProps) {
  const [clock, setClock] = useState(() => formatClockTime(new Date()));
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Tick the wall-clock every second while mounted. Skipped entirely when a
  // scheduled label is supplied — there is nothing to tick, and no reason to
  // run a timer.
  useEffect(() => {
    if (scheduledLabel != null) return;
    const id = window.setInterval(() => {
      setClock(formatClockTime(new Date()));
    }, 1000);
    return () => window.clearInterval(id);
  }, [scheduledLabel]);

  // Move focus onto Close on mount.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Trap focus on the single Close button — Tab / Shift+Tab keep it here.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    closeRef.current?.focus();
  };

  const cls = `${styles.overlay}${isKiosk ? ` ${styles.kiosk}` : ""}${
    reserveAlarmBand ? ` ${styles.reserveAlarmBand}` : ""
  }`;

  return (
    <div
      className={cls}
      role="dialog"
      aria-modal="true"
      aria-label="Reminder"
      onKeyDown={onKeyDown}
    >
      <div className={styles.time}>
        {scheduledLabel != null ? `Reminder for ${scheduledLabel}` : clock}
      </div>
      <div className={styles.message}>{reminder.message}</div>
      <div className={styles.buttons}>
        <button
          ref={closeRef}
          type="button"
          className={`${styles.btn} ${styles.close}`}
          aria-label="Close reminder"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}
